<template>
  <div>
    <router-view></router-view>
    <index-card :loading="loading">
      <template slot="header">
        <div class="index-clients-header d-flex align-center justify-space-between flex-wrap" style="width: 100%; gap: 8px;">
          <div class="d-flex align-center flex-shrink-0">
            <v-btn text icon large to="/" exact><v-icon class="white--text">mdi-arrow-left</v-icon></v-btn>
            <v-btn color="white" class="ml-1 blue-grey--text elavation-0 font-weight-bold" @click="$router.push({ name: 'CreateClient' })">
              <v-icon small left>mdi-plus</v-icon>Novo Cliente
            </v-btn>
          </div>

          <div class="index-clients-title-block text-center flex-grow-1 px-2" style="min-width: 160px;">
            <div class="headline white--text font-weight-medium mb-0">Clientes</div>
            <p class="index-clients-subtitle mb-0 mt-1 white--text">
              {{ clientsSubtitle }}
            </p>
          </div>

          <div class="d-flex align-center flex-shrink-0">
            <BtnCreateDataExport
              v-if="canExportClients"
              class="mx-3"
              api_model="Client"
              action="Clients"
              :filters="export_filters"
            />
            <v-btn text icon color="white" aria-label="Atualizar lista" @click="getData()">
              <v-icon class="white--text">mdi-refresh</v-icon>
            </v-btn>
          </div>
        </div>
      </template>
      <!-- subheader fica montado mesmo com loading do IndexCard (evita loop destroy/remount do FilterData) -->
      <template slot="subheader">
        <div class="index-clients-filters">
          <filter-data
            with_client_portfolios
            :with_client="false"
            :with_date_start="false"
            :with_date_end="false"
            with_text_search
            text_search_label="Pesquise.."
            :loading_block="loading"
            @doneFilter="handleDoneFilter"
          />
        </div>
      </template>
      <template slot="data_table">
        <v-data-table 
          @click:row="editItem" 
          :headers="headers" 
          :items="clients"
          :search="tableSearch"
          :loading="loading"
          class="elevation-4" 
          mobile-breakpoint="1"
        >
          <template v-slot:item.attributes.name="{item}">
            <v-tooltip top color="black">
              <template v-slot:activator="{ on }">
                <v-icon v-on="on" small color="red" v-if="item.attributes.sellers_qtd > 1">mdi-account-multiple</v-icon>
              </template>
              Vários vendedores! Clique para editar.
            </v-tooltip>
            {{ item.attributes.name }}
          </template>
          <template v-slot:item.attributes.doc_number="{item}">
            {{ item.attributes.doc_number, item.attributes.cpf_or_cnpj | DocumentPersonBr}}
          </template>
          <template v-slot:item.attributes.status_name="{item}">
            <v-row>
              <v-col cols="12" class="pa-0">
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon :color="item.attributes.is_blocked == 'Sim' ? 'error' : 'primary'" dark v-bind="attrs" v-on="on">{{ item.attributes.is_blocked == "Sim" ? 'mdi-cancel': 'mdi-check'}}</v-icon>
                  </template>
                  <h4>Cliente bloqueado: {{  item.attributes.is_blocked }}</h4>
                </v-tooltip>

                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon :color="item.attributes.has_overdue_trans == 'Sim' ? 'error' : 'success'" dark v-bind="attrs" v-on="on">{{ item.attributes.has_overdue_trans == "Sim" ? 'mdi-currency-usd-off': 'mdi-currency-usd'}}</v-icon>
                  </template>
                  <h4>Faturas atrasadas: {{  item.attributes.has_overdue_trans }}</h4>
                </v-tooltip>
              </v-col>
            </v-row>
          
          </template>
          <template v-slot:item.attributes.limit="{item}">
            <span v-if="item.attributes.limit" style="text-wrap: nowrap;"> 
              R$ {{ item.attributes.limit.replace(/\d(?=(\d{3})+\.)/g, '$&,') }} 
            </span>
          </template>
          <template v-slot:item.attributes.blockingTime="{item}">
            <span v-if="item.attributes.blockingTime != null"> {{item.attributes.blockingTime}} dias </span>
          </template>
          <template v-slot:no-results>
            <v-alert  :value="true" type="error" icon="mdi-alert">
              Não foi possível encontrar "{{ tableSearch }}".
            </v-alert>
          </template>
        </v-data-table>
      </template>
    </index-card>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import Api from '@/api/index';

export default {
  name:"IndexClients",
  components: {
    ShowClient: () => import("@/_backoffice/_registrations/components/client/show/ShowClient.vue"),
    BtnCreateDataExport: () => import('@/components/data_export/BtnCreateDataExport.vue'),
    IndexCard: () => import("@/_backoffice/_registrations/components/shared/index/IndexCard.vue"),
    FilterData: () => import('@/components/shared/FilterData.vue'),
  },
  data() {
    return {
      local_filter: {},
      levels: {
        0: { color: 'yellow', limiter: 0, active: true, description: "Nenhuma atividade" },
        1: { color: 'green', limiter: 16, active: true, description: "Ativo (0 - 15)" },
        2: { color: 'orange', limiter: 31, active: true, description: "(16 - 30)" },
        3: { color: 'red', limiter: 61, active: true, description: "(31 - 60)" },
        4: { color: 'purple', limiter: -1, active: true, description: "Inativo (+ 61)" },
      },
      status_map: {
        18: {color: 'red', description: 'Cliente bloqueado manualmente.', icon: 'mdi-currency-usd-off'},
        10: {color: 'red', description: 'Cliente bloqueado. Favor entrar em contato.', icon: 'mdi-currency-usd-off'},
        9: {color: 'orange', description: 'Cliente bloqueado. Faturas atrasadas.', icon: 'mdi-currency-usd-off'},
        1: {color: 'success', description: 'Pagamentos regulares.', icon: 'mdi-currency-usd'},
      },
      headers: [
        { text: 'ID', value: 'attributes.id', align: 'center' },
        { text: 'Rz Social / Nome', value: 'attributes.corporateName', align: 'center' },
        { text: 'Nome Fantasia', value: 'attributes.fantasyName', align: 'center' },
        { text: 'CPF/CNPJ', value: 'attributes.doc_number', align: 'center' },
        { text: 'Status', value: 'attributes.status_name', align: 'center' },
        { text: 'Cidade', value: 'attributes.city', align: 'center' },
        { text: 'Limite de Crédito', value: 'attributes.limit', align: 'center' },
        { text: 'Tempo bloqueio', value: 'attributes.blockingTime', align: 'center' },
      ],
      letEdit: false,
      isOpen: false,
      selected_modality_id: null,
      loading: false,
      clients: []
    }
  },

  methods: {
    getData(){
      this.getClients()
    },
    handleDoneFilter(filter){
      this.local_filter = { ...filter }
      this.getClients()
    },
    getActivityLevel(client) {
      var activity = client.attributes.activity
      if (!activity) { return this.levels[0] }
      return activity < this.levels[1]["limiter"] ? this.levels[1] : activity < this.levels[2]["limiter"] ? this.levels[2] : activity < this.levels[3]["limiter"] ? this.levels[3] : this.levels[4]
    },
    editItem(item) {
      this.$router.push({name:"ShowClient", params:{id: item.id}})
    },
    getClients(){
      if(this.activeModalityId){
        this.loading = true
        let params = {
          modality_id: this.activeModalityId,
          short_index: true,
          ...this.local_filter,
        }
        // busca textual é local na tabela; não envia query para a API de index curto
        delete params.query
        Api.Client.index(params).then(r=> {
          this.clients =  r.data.data
        }).catch(err=>{
          console.log(err)
        }).finally(() => {
          this.loading = false
        })
      }
    },
  },
  computed: {
    tableSearch() {
      return (this.local_filter && this.local_filter.query) ? String(this.local_filter.query) : ''
    },
    export_filters() {
      return {
        modality_id: this.activeModalityId || this.selected_modality_id,
        ...(this.local_filter?.portfolio_id ? { portfolio_id: this.local_filter.portfolio_id } : {}),
      }
    },
    clientsSubtitle() {
      if (this.loading) return 'Carregando lista…';
      const n = Array.isArray(this.clients) ? this.clients.length : 0;
      if (n === 0) return 'Nenhum cliente nesta visualização';
      const fmt = n.toLocaleString('pt-BR');
      return n === 1 ? `${fmt} cliente listado` : `${fmt} clientes listados`;
    },
    canExportClients(){
      return this.isDirector()
    },
    ...mapState({
      oneClient: state => state.Client.oneClient,
    }),
  },
  // Carga inicial vem do FilterData (@doneFilter no mount)
  watch:{
    activeModalityId(nv){
      if (nv) this.getClients()
    },
  }
}
</script>

<style scoped>
.index-clients-subtitle {
  font-size: 0.8125rem;
  line-height: 1.35;
  opacity: 0.88;
  font-weight: 400;
}
.index-clients-filters {
  width: 100%;
  flex: 1 1 100%;
  min-width: 0;
}
.index-clients-filters >>> .filter-data-card {
  width: 100%;
}
</style>
